import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import type { Feature } from "geojson";
import moment from "moment";
import { observer } from "mobx-react-lite";
import Dialog from "@components/Dialog";
import Text from "@components/Text";
import SizedBox from "@components/SizedBox";
import { Row, Column } from "@components/Flex";
import Button from "@components/Button";
import Select from "@components/Select";
import Spinner from "@components/Spinner";
import Checkbox from "@components/Checkbox";
import { useLang, useArea, useConvert } from "@src/hooks/useLang";
import useIsMobile from "@src/hooks/useIsMobile";
import { useFieldsScreenVM } from "@screens/FieldsScreen/FieldsScreenVM";
import {
  getFieldsFromTracks,
  TFieldFromTracksCandidate,
  TCompass8,
} from "@services/fieldsFromTracksService";
import FromTracksPreviewMap, { TPreviewCandidate } from "./FromTracksPreviewMap";

interface IProps {
  visible: boolean;
  seasonId: string;
  /** Creates the fields; resolves when done (the caller reports the outcome). */
  onSubmit: (features: Feature[], cropId: string) => Promise<unknown>;
  onClose: () => void;
}

type TPhase = "scanning" | "failed" | "ready";
type TRowState = { selected: boolean; name: string };

/** The season scan runs server-side; this is how often we ask if it's done. */
const POLL_MS = 3000;

const NameInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 34px;
  padding: 0 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  color: #494c5b;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: #68ad18;
  }
  &:disabled {
    background: #f9fafb;
    color: #9ca3af;
  }
`;

const CandidateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-of-type {
    border-bottom: none;
  }
`;

const Meta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  text-align: right;
`;

const fill = (template: string, values: Record<string, string | number>) =>
  Object.entries(values).reduce((s, [k, v]) => s.split(`{${k}}`).join(String(v)), template);

const FromTracksDialog: React.FC<IProps> = ({ visible, seasonId, onSubmit, onClose }) => {
  const lang = useLang();
  const fieldsLang = lang.fields as any;
  const locArea = useArea();
  const { convertArea } = useConvert();
  const isMobile = useIsMobile();
  const vm = useFieldsScreenVM();

  const [phase, setPhase] = useState<TPhase>("scanning");
  const [attempt, setAttempt] = useState(0);
  const [startedAt, setStartedAt] = useState<string | null>(null);
  const [computedAt, setComputedAt] = useState<string | null>(null);
  // Set by "Try again" / "Scan again": the next request asks for a new scan.
  const rescanNext = useRef(false);
  const [candidates, setCandidates] = useState<TFieldFromTracksCandidate[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [rows, setRows] = useState<Record<string, TRowState>>({});
  const [cropId, setCropId] = useState("");
  const [saving, setSaving] = useState(false);

  // Read inside the polling effect without making it re-run: useLang and the
  // field list are not stable references, and a re-run restarts the poll.
  const langRef = useRef(fieldsLang);
  langRef.current = fieldsLang;
  const existingNamesRef = useRef<string[]>([]);
  existingNamesRef.current = vm.fields.map((f) => String(f.get("name") ?? ""));

  useEffect(() => {
    if (!visible || !seasonId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setPhase("scanning");
    setCandidates([]);
    setStartedAt(null);

    const poll = () => {
      const rescan = rescanNext.current;
      rescanNext.current = false;
      return getFieldsFromTracks(seasonId, rescan)
        .then((r) => {
          if (cancelled) return;
          if (r.status === "running") {
            setStartedAt(r.startedAt);
            timer = setTimeout(poll, POLL_MS);
            return;
          }
          if (r.status === "failed") {
            setPhase("failed");
            return;
          }
          setRows(initialRows(r.candidates, langRef.current, existingNamesRef.current));
          setCandidates(r.candidates);
          setTruncated(r.truncated);
          setComputedAt(r.computedAt);
          setPhase("ready"); // last: every render in "ready" has rows to show
        })
        // The server's reason is English and not the user's to act on; the
        // localized message plus "Try again" is the whole story.
        .catch(() => {
          if (!cancelled) setPhase("failed");
        });
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [visible, seasonId, attempt]);

  // Default crop: whatever most of the season's fields already grow, so an
  // all-beet farm isn't handed "Alfalfa" because it sorts first.
  const cropIds = vm.crops.map((c) => c.id).join(",");
  const commonCrop = useMemo(() => {
    const counts = new Map<string, number>();
    for (const f of vm.fields) {
      const id = f.get("culture_id");
      if (id) counts.set(String(id), (counts.get(String(id)) ?? 0) + 1);
    }
    const known = new Set(cropIds.split(","));
    const best = [...counts.entries()].filter(([id]) => known.has(id)).sort((a, b) => b[1] - a[1])[0];
    return best?.[0] ?? cropIds.split(",")[0] ?? "";
  }, [vm.fields, cropIds]);

  useEffect(() => {
    if (visible) setCropId(commonCrop);
  }, [visible, commonCrop]);

  const selectable = candidates.filter((c) => !c.exceedsMaxArea);
  const selectedCount = selectable.filter((c) => rows[c.id]?.selected).length;
  const allSelected = selectable.length > 0 && selectedCount === selectable.length;

  const scanAgain = () => {
    rescanNext.current = true;
    setAttempt((n) => n + 1);
  };

  const toggle = (id: string) =>
    setRows((prev) => ({ ...prev, [id]: { ...prev[id], selected: !prev[id]?.selected } }));
  const toggleAll = () =>
    setRows((prev) => {
      const next = { ...prev };
      for (const c of selectable) next[c.id] = { ...next[c.id], selected: !allSelected };
      return next;
    });
  const rename = (id: string, name: string) =>
    setRows((prev) => ({ ...prev, [id]: { ...prev[id], name } }));

  const preview: TPreviewCandidate[] = candidates.map((c) => ({
    id: c.id,
    geometry: c.geometry,
    name: rows[c.id]?.name ?? "",
    selected: Boolean(rows[c.id]?.selected),
    disabled: c.exceedsMaxArea,
  }));

  const handleSave = () => {
    const features: Feature[] = selectable
      .filter((c) => rows[c.id]?.selected)
      .map((c) => ({
        type: "Feature",
        geometry: c.geometry,
        properties: { name: rows[c.id].name.trim() || defaultName(c, fieldsLang) },
      }));
    if (features.length === 0 || !cropId) return;
    setSaving(true);
    onSubmit(features, cropId).finally(() => setSaving(false));
  };

  const areaLabel = (ha: number) => `${convertArea(ha).toFixed(1)} ${locArea()}`;
  const whenLabel = (c: TFieldFromTracksCandidate) => {
    const a = moment(c.firstAt);
    const b = moment(c.lastAt);
    return a.isSame(b, "day") ? a.format("MMM D") : `${a.format("MMM D")} – ${b.format("MMM D")}`;
  };

  const renderScanning = () => (
    <Column crossAxisSize="max" alignItems="center" style={{ padding: 32, gap: 12 }}>
      <Spinner />
      <Text size={14} weight={500}>
        {fieldsLang.fromTracksScanning ?? "Looking for harvested areas without boundaries…"}
      </Text>
      <Text size={13} color="#6B7280" style={{ textAlign: "center" }}>
        {fieldsLang.fromTracksScanningHint ??
          "Scanning a whole season of tracks can take several minutes."}
      </Text>
      <Text size={13} color="#6B7280" style={{ textAlign: "center" }}>
        {fieldsLang.fromTracksLeaveHint ??
          "You can close this window — the scan keeps running. Open New field → From tracks again later to see the result."}
      </Text>
      {startedAt && (
        <Text size={12} color="#9CA3AF">
          {fill(fieldsLang.fromTracksStarted ?? "Started {time}", { time: moment(startedAt).fromNow() })}
        </Text>
      )}
      <Button kind="outline" onClick={onClose}>
        {fieldsLang.fromTracksClose ?? "Close"}
      </Button>
    </Column>
  );

  const renderFailed = () => (
    <Column crossAxisSize="max" alignItems="center" style={{ padding: 24, gap: 12 }}>
      <Text size={14} weight={500}>
        {fieldsLang.fromTracksFailed ?? "Couldn't scan this season's tracks."}
      </Text>
      <Row justifyContent="center" style={{ gap: 8 }}>
        <Button kind="outline" onClick={onClose}>
          {lang.fields.drawFieldsCancelButton}
        </Button>
        <Button onClick={scanAgain}>
          {fieldsLang.fromTracksRetry ?? "Try again"}
        </Button>
      </Row>
    </Column>
  );

  const renderEmpty = () => (
    <Column crossAxisSize="max" alignItems="center" style={{ padding: 24, gap: 16 }}>
      <Text size={14} style={{ textAlign: "center" }}>
        {fieldsLang.fromTracksEmpty ??
          "No harvested areas outside your fields were found this season."}
      </Text>
      <Row justifyContent="center" style={{ gap: 8 }}>
        <Button kind="outline" onClick={onClose}>
          {fieldsLang.fromTracksClose ?? "Close"}
        </Button>
        <Button kind="outline" onClick={scanAgain}>
          {fieldsLang.fromTracksRescan ?? "Scan again"}
        </Button>
      </Row>
    </Column>
  );

  const renderReady = () => (
    <Column crossAxisSize="max">
      <Text size={13} color="#6B7280">
        {fieldsLang.fromTracksHint ??
          "Each area is a field your machines harvested this season that has no boundary yet. Click an area on the map or untick it to leave it out, and rename the ones you keep."}
      </Text>
      {computedAt && (
        <Row alignItems="center" justifyContent="space-between" style={{ gap: 8, marginTop: 6 }}>
          <Text size={12} color="#9CA3AF">
            {fill(fieldsLang.fromTracksScannedAt ?? "Scanned {time}", { time: moment(computedAt).fromNow() })}
          </Text>
          <Button kind="outline" onClick={scanAgain} disabled={saving} style={{ height: 32, padding: "0 12px", width: "auto" }}>
            {fieldsLang.fromTracksRescan ?? "Scan again"}
          </Button>
        </Row>
      )}
      <SizedBox height={12} />
      <FromTracksPreviewMap
        candidates={preview}
        existing={vm.fields}
        onToggle={toggle}
        height={isMobile ? 220 : 300}
      />
      <SizedBox height={12} />
      <Row alignItems="center" style={{ gap: 8 }}>
        <Checkbox checked={allSelected} onChange={toggleAll} />
        <Text size={13} color="#6B7280">
          {fill(fieldsLang.fromTracksSelectedCount ?? "{selected} of {total} selected", {
            selected: selectedCount,
            total: selectable.length,
          })}
        </Text>
      </Row>
      <SizedBox height={4} />
      <div style={{ maxHeight: isMobile ? 180 : 220, overflowY: "auto", flexShrink: 0, width: "100%" }}>
        {candidates.map((c) => {
          const row = rows[c.id];
          return (
            <CandidateRow key={c.id}>
              {c.exceedsMaxArea ? (
                <span style={{ width: 20, flexShrink: 0 }} />
              ) : (
                <Checkbox checked={Boolean(row?.selected)} onChange={() => toggle(c.id)} />
              )}
              <NameInput
                value={row?.name ?? ""}
                onChange={(e) => rename(c.id, e.target.value)}
                disabled={c.exceedsMaxArea || !row?.selected}
                aria-label={lang.fields.newFieldName}
              />
              <Meta>
                <Text size={13} weight={500}>
                  {areaLabel(c.areaHa)}
                </Text>
                <Text size={12} color="#6B7280">
                  {c.exceedsMaxArea
                    ? fieldsLang.fromTracksTooLarge ?? "Too large for one field"
                    : [whenLabel(c), ...c.devices.map((d) => d.name).filter(Boolean)].join(" · ")}
                </Text>
              </Meta>
            </CandidateRow>
          );
        })}
      </div>
      {truncated && (
        <>
          <SizedBox height={8} />
          <Text size={12} color="#B45309">
            {fieldsLang.fromTracksTruncated ??
              "This season has more track data than one scan can hold, so later areas may be missing."}
          </Text>
        </>
      )}
      <SizedBox height={16} />
      <Select
        label={fieldsLang.newFieldCrop ?? "Crop"}
        value={cropId}
        onChange={setCropId}
        options={vm.crops.map((crop) => ({ title: crop.name, value: crop.id }))}
      />
      <SizedBox height={20} />
      <Row justifyContent="flex-end" style={{ gap: 8 }}>
        <Button kind="outline" onClick={onClose} disabled={saving}>
          {lang.fields.drawFieldsCancelButton}
        </Button>
        <Button onClick={handleSave} disabled={selectedCount === 0 || !cropId || saving}>
          {saving
            ? fieldsLang.uploadImporting ?? "Importing…"
            : fill(fieldsLang.fromTracksCreate ?? "Create fields ({count})", { count: selectedCount })}
        </Button>
      </Row>
    </Column>
  );

  return (
    <Dialog
      title={fieldsLang.fromTracksTitle ?? "Fields from tracks"}
      visible={visible}
      onClose={onClose}
      style={{ maxWidth: 640 }}
    >
      {phase === "scanning" && renderScanning()}
      {phase === "failed" && renderFailed()}
      {phase === "ready" && (candidates.length === 0 ? renderEmpty() : renderReady())}
    </Dialog>
  );
};

/** "Gust 15 East" beside a known field, otherwise "Harvested Sep 22". */
function defaultName(c: TFieldFromTracksCandidate, fieldsLang: any): string {
  if (c.nearField) {
    const dir = fieldsLang[`fromTracksDir${c.nearField.direction}`] ?? DIRECTIONS_EN[c.nearField.direction];
    return fill(fieldsLang.fromTracksNameNear ?? "{name} {direction}", {
      name: c.nearField.name,
      direction: dir,
    });
  }
  return fill(fieldsLang.fromTracksNameHarvested ?? "Harvested {date}", {
    date: moment(c.firstAt).format("MMM D"),
  });
}

const DIRECTIONS_EN: Record<TCompass8, string> = {
  N: "North",
  NE: "Northeast",
  E: "East",
  SE: "Southeast",
  S: "South",
  SW: "Southwest",
  W: "West",
  NW: "Northwest",
};

/** Everything that can be created starts ticked, each with a unique default name. */
function initialRows(
  candidates: TFieldFromTracksCandidate[],
  fieldsLang: any,
  existingNames: string[]
): Record<string, TRowState> {
  const taken = new Set(existingNames.map((n) => n.trim().toLowerCase()));
  const rows: Record<string, TRowState> = {};
  for (const c of candidates) {
    const base = defaultName(c, fieldsLang);
    let name = base;
    for (let n = 2; taken.has(name.toLowerCase()); n++) name = `${base} ${n}`;
    taken.add(name.toLowerCase());
    rows[c.id] = { selected: !c.exceedsMaxArea, name };
  }
  return rows;
}

export default observer(FromTracksDialog);
